import { isNativePlatform } from '../platform';
import { getOfflineDbAtWipeEpoch, getWipeEpoch } from './connection';
import { isGateEnabled } from './meta';
import { replaceOrgMemberships, upsertBootstrapProfile } from './repositories/core';

import type { OrganizationMember } from '@/contexts/organization-types';

export interface CachedUserProfile {
  readonly id: string;
  readonly full_name: string | null;
  readonly username: string | null;
  readonly avatar_url: string | null;
  readonly updated_at: string | null;
  readonly role: string | null;
}

export interface NativeBootstrapWriteGeneration {
  readonly authSessionId: string;
  readonly generation: number;
  readonly ownerUserId: string;
  readonly wipeEpoch: number;
}

export interface NativeBootstrapMembershipWriteGeneration extends NativeBootstrapWriteGeneration {
  readonly membershipGeneration: number;
}

export interface NativeBootstrapMembershipProofGeneration extends NativeBootstrapMembershipWriteGeneration {
  readonly activeOrganizationIds: ReadonlySet<string>;
}

interface ActiveBootstrapWriteState {
  readonly token: NativeBootstrapWriteGeneration;
  membershipGeneration: number;
  memberships: readonly OrganizationMember[] | null;
}

let activeWriteState: ActiveBootstrapWriteState | null = null;
let nextWriteGeneration = 0;
let membershipWriteTail: Promise<void> = Promise.resolve();

function activeStateForGeneration(
  generation: NativeBootstrapWriteGeneration | null | undefined,
  ownerUserId: string,
): ActiveBootstrapWriteState | null {
  const state = activeWriteState;
  if (generation === null || generation === undefined) {
    return null;
  }
  if (
    generation.ownerUserId !== ownerUserId ||
    generation.wipeEpoch !== getWipeEpoch() ||
    state?.token.generation !== generation.generation ||
    state.token.authSessionId !== generation.authSessionId ||
    state.token.ownerUserId !== ownerUserId
  ) {
    return null;
  }
  return state;
}

function activeStateForMembershipGeneration(
  generation:
    | NativeBootstrapMembershipWriteGeneration
    | NativeBootstrapMembershipProofGeneration
    | null
    | undefined,
  ownerUserId: string,
): ActiveBootstrapWriteState | null {
  const state = activeStateForGeneration(generation, ownerUserId);
  return state?.membershipGeneration === generation?.membershipGeneration ? state : null;
}

/** Establish the cache-write authority for one native auth session. */
export function activateNativeBootstrapWriteGeneration(
  ownerUserId: string,
  authSessionId: string,
): NativeBootstrapWriteGeneration | null {
  if (!isNativePlatform() || !ownerUserId || !authSessionId) {
    return null;
  }
  nextWriteGeneration += 1;
  const token: NativeBootstrapWriteGeneration = {
    authSessionId,
    generation: nextWriteGeneration,
    ownerUserId,
    wipeEpoch: getWipeEpoch(),
  };
  activeWriteState = { token, membershipGeneration: 0, memberships: null };
  return token;
}

export function deactivateNativeBootstrapWriteGeneration(
  generation: NativeBootstrapWriteGeneration | null,
): void {
  if (activeWriteState?.token.generation === generation?.generation) {
    activeWriteState = null;
  }
}

/** Capture authority only when this exact user/session still owns native cache writes. */
export function captureNativeBootstrapSessionWriteGeneration(
  ownerUserId: string,
  authSessionId: string,
): NativeBootstrapWriteGeneration | null {
  const state = activeWriteState;
  if (state === null) {
    return null;
  }
  if (
    state.token.authSessionId !== authSessionId ||
    !activeStateForGeneration(state.token, ownerUserId)
  ) {
    return null;
  }
  return state.token;
}

/** Capture membership request authority before its server read begins. */
export function captureNativeBootstrapMembershipWriteGeneration(
  generation: NativeBootstrapWriteGeneration | null,
): NativeBootstrapMembershipWriteGeneration | null {
  if (!generation) {
    return null;
  }
  const state = activeStateForGeneration(generation, generation.ownerUserId);
  if (!state) {
    return null;
  }
  return { ...generation, membershipGeneration: state.membershipGeneration };
}

async function runSerializedMembershipWrite(work: () => Promise<void>): Promise<void> {
  const run = membershipWriteTail.then(work);
  membershipWriteTail = run.catch((_error: unknown): void => undefined);
  await run;
}

function isCompleteMembershipSnapshot(
  ownerUserId: string,
  memberships: readonly OrganizationMember[],
): boolean {
  return memberships.every(
    (membership) =>
      membership.user_id === ownerUserId &&
      membership.status === 'active' &&
      membership.organization_id !== '' &&
      membership.organization?.id === membership.organization_id,
  );
}

async function writeMembershipSnapshot(
  ownerUserId: string,
  generation: NativeBootstrapMembershipWriteGeneration | NativeBootstrapMembershipProofGeneration,
  memberships: readonly OrganizationMember[],
  activeOrganizationIds?: ReadonlySet<string>,
): Promise<void> {
  await runSerializedMembershipWrite(async () => {
    if (!activeStateForMembershipGeneration(generation, ownerUserId)) {
      return;
    }
    const cache = await getOfflineDbAtWipeEpoch(ownerUserId, generation.wipeEpoch);
    if (!cache || !activeStateForMembershipGeneration(generation, ownerUserId)) {
      return;
    }
    const enabledMemberships: OrganizationMember[] = [];
    for (const membership of memberships) {
      if (
        (!activeOrganizationIds || activeOrganizationIds.has(membership.organization_id)) &&
        (await isGateEnabled(cache, membership.organization_id))
      ) {
        enabledMemberships.push(membership);
      }
      if (!activeStateForMembershipGeneration(generation, ownerUserId)) {
        return;
      }
    }
    if (!activeStateForMembershipGeneration(generation, ownerUserId)) {
      return;
    }
    await replaceOrgMemberships(
      cache,
      enabledMemberships.map((membership) => ({
        id: membership.organization_id,
        payload: JSON.stringify(membership),
      })),
    );
  });
}

/** Invalidate membership responses older than this authoritative proof. */
export async function beginNativeBootstrapMembershipProof(
  ownerUserId: string,
  activeOrganizationIds: ReadonlySet<string>,
): Promise<NativeBootstrapMembershipProofGeneration | null> {
  const state = activeWriteState;
  if (!state || !activeStateForGeneration(state.token, ownerUserId)) {
    return null;
  }
  state.membershipGeneration += 1;
  const proof: NativeBootstrapMembershipProofGeneration = {
    ...state.token,
    membershipGeneration: state.membershipGeneration,
    activeOrganizationIds: new Set(activeOrganizationIds),
  };
  const writesBeforeProof = membershipWriteTail;
  await writesBeforeProof;
  return activeStateForMembershipGeneration(proof, ownerUserId) ? proof : null;
}

/** Persist the latest snapshot, intersected with authoritative proof and gates. */
export async function commitNativeBootstrapMembershipProof(
  proof: NativeBootstrapMembershipProofGeneration | null,
): Promise<void> {
  if (!proof) {
    return;
  }
  const state = activeStateForMembershipGeneration(proof, proof.ownerUserId);
  if (!state?.memberships) {
    return;
  }
  try {
    await writeMembershipSnapshot(
      proof.ownerUserId,
      proof,
      state.memberships,
      proof.activeOrganizationIds,
    );
  } catch {
    // The cache is disposable; a failed recache never weakens the proof/purge.
  }
}

/** Persist a server-authorised profile response into the encrypted cache. */
export async function cacheNativeBootstrapProfile(
  userId: string,
  profile: CachedUserProfile,
  generation?: NativeBootstrapWriteGeneration | null,
): Promise<void> {
  if (!isNativePlatform() || !generation || !activeStateForGeneration(generation, userId)) {
    return;
  }
  try {
    const cache = await getOfflineDbAtWipeEpoch(userId, generation.wipeEpoch);
    if (cache && activeStateForGeneration(generation, userId)) {
      await upsertBootstrapProfile(cache, userId, JSON.stringify(profile));
    }
  } catch {
    // The cache is disposable; the authenticated response remains usable.
  }
}

/** Persist the complete server-authorised active-membership snapshot. */
export async function cacheNativeBootstrapMemberships(
  userId: string,
  memberships: readonly OrganizationMember[],
  generation?: NativeBootstrapMembershipWriteGeneration | null,
): Promise<void> {
  const state = activeStateForMembershipGeneration(generation, userId);
  if (!isNativePlatform() || !state || !generation) {
    return;
  }
  if (!isCompleteMembershipSnapshot(userId, memberships)) {
    return;
  }
  state.memberships = [...memberships];
  try {
    await writeMembershipSnapshot(userId, generation, memberships);
  } catch {
    // The cache is disposable; the authenticated response remains usable.
  }
}

export function __resetNativeBootstrapWriteGenerationForTests(): void {
  activeWriteState = null;
  nextWriteGeneration = 0;
  membershipWriteTail = Promise.resolve();
}
